import { useRef, useEffect, useState } from 'react';

const milestones = [
  { icon: 'ri-building-line', stat: 'Residential', label: 'Homes Transformed' },
  { icon: 'ri-store-2-line', stat: 'Commercial', label: 'Business Spaces Floored' },
  { icon: 'ri-anchor-line', stat: 'Marine', label: 'Specialty Installations' },
  { icon: 'ri-shield-check-line', stat: 'Licensed', label: '& Fully Insured' },
];

const values = [
  { icon: 'ri-award-line', title: 'Commitment to Quality', desc: 'Every project is approached with the same meticulous attention — whether a single room or an entire building.' },
  { icon: 'ri-handshake-line', title: 'Honest Service', desc: 'We provide straightforward guidance, accurate quotes, and transparent communication throughout every project.' },
  { icon: 'ri-map-pin-line', title: 'Local Expertise', desc: 'Based in Freehold, NJ, we understand the homes and businesses in our community — and take pride in serving them well.' },
];

export default function CompanyOverview() {
  const ref = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-24 lg:py-32 bg-cream">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Intro split */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
          <div
            style={{
              opacity: inView ? 1 : 0,
              transform: inView ? 'translateX(0)' : 'translateX(-28px)',
              transition: 'opacity 0.8s ease, transform 0.8s ease',
            }}
          >
            <span className="section-tag mb-4 block">Our Story</span>
            <h2 className="section-heading mb-6">
              Dedicated to Quality Flooring<br />
              <em className="text-gold not-italic">& Reliable Service</em>
            </h2>
            <p className="text-warm-gray text-base leading-relaxed mb-6">
              Value Carpet Inc is a specialty flooring company based in Freehold, New Jersey. We provide professional flooring installation services for both residential homeowners and commercial businesses across Monmouth County and surrounding regions.
            </p>
            <p className="text-warm-gray text-base leading-relaxed mb-10">
              Our focus is simple: deliver exceptional quality on every project. Whether you&apos;re transforming a single room or fitting out an entire commercial space, we bring the same dedication to precision, material quality, and customer satisfaction.
            </p>
            <div className="gold-line" />
          </div>

          <div
            className="rounded-lg overflow-hidden"
            style={{
              height: '520px',
              opacity: inView ? 1 : 0,
              transform: inView ? 'translateX(0)' : 'translateX(28px)',
              transition: 'opacity 0.8s ease 0.15s, transform 0.8s ease 0.15s',
            }}
          >
            <img
              src="https://public.readdy.ai/ai/img_res/70ef55a6f606158940066a55a4d2eb81.jpg"
              alt="Value Carpet Inc — Quality flooring in New Jersey"
              className="w-full h-full object-cover object-center"
            />
          </div>
        </div>

        {/* Stats strip */}
        <div
          className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-24"
          style={{
            opacity: inView ? 1 : 0,
            transition: 'opacity 0.7s ease 0.2s',
          }}
        >
          {milestones.map((m, i) => (
            <div
              key={m.label}
              className="bg-white rounded-lg p-7 text-center border border-cream-dark hover:border-gold/30 transition-colors"
              style={{
                opacity: inView ? 1 : 0,
                transform: inView ? 'translateY(0)' : 'translateY(20px)',
                transition: `opacity 0.5s ease ${0.2 + i * 0.08}s, transform 0.5s ease ${0.2 + i * 0.08}s`,
              }}
            >
              <span className="w-10 h-10 flex items-center justify-center rounded-full bg-cream mx-auto mb-4">
                <i className={`${m.icon} text-gold text-base`}></i>
              </span>
              <p className="font-serif text-xl text-charcoal font-semibold mb-1">{m.stat}</p>
              <p className="text-warm-gray text-xs">{m.label}</p>
            </div>
          ))}
        </div>

        {/* Values */}
        <div
          className="text-center mb-12"
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.7s ease 0.3s, transform 0.7s ease 0.3s',
          }}
        >
          <span className="section-tag mb-4 block">Our Values</span>
          <h2 className="section-heading">What Drives Everything We Do</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {values.map((v, i) => (
            <div
              key={v.title}
              className="bg-white rounded-lg p-8 border border-cream-dark"
              style={{
                opacity: inView ? 1 : 0,
                transform: inView ? 'translateY(0)' : 'translateY(24px)',
                transition: `opacity 0.6s ease ${0.35 + i * 0.1}s, transform 0.6s ease ${0.35 + i * 0.1}s`,
              }}
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-charcoal mb-5">
                <i className={`${v.icon} text-gold text-lg`}></i>
              </div>
              <h3 className="font-serif text-xl text-charcoal mb-3">{v.title}</h3>
              <p className="text-warm-gray text-sm leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
